import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logger from '../utils/logger';

/**
 * ページ遷移を追跡するカスタムフック
 */
export const usePageTracking = (): void => {
  const location = useLocation();
  const { user, store } = useAuth();

  useEffect(() => {
    // ページ名をパスから取得
    const getPageName = (pathname: string): string => {
      const pageMap: Record<string, string> = {
        '/': 'メインページ',
        '/customers': '顧客管理',
        '/deposit': '入金',
        '/payment': '精算',
        '/history': '履歴'
      };
      return pageMap[pathname] || '不明なページ';
    };

    const pageName = getPageName(location.pathname);

    // ページ遷移をログ
    logger.userAction('page_view', {
      pageName,
      pathname: location.pathname,
      search: location.search,
      userId: user?.userId,
      userName: user?.name,
      storeId: store?.id,
      storeName: store?.name,
      timestamp: new Date().toISOString(),
      referrer: document.referrer,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    });

    // ページビューをinfo レベルでもログ
    logger.info(`Page view: ${pageName}`, {
      context: 'navigation',
      pathname: location.pathname,
      userId: user?.userId,
      storeId: store?.id
    });

    // パフォーマンス計測（ナビゲーションタイミング）
    if (window.performance?.getEntriesByType) {
      const navEntries = window.performance.getEntriesByType('navigation');
      if (navEntries.length > 0) {
        const navTiming = navEntries[0] as PerformanceNavigationTiming;

        // すべての必要なプロパティが存在し、0より大きいことを確認
        const loadEventEnd = navTiming.loadEventEnd || 0;
        const fetchStart = navTiming.fetchStart || 0;
        const domContentLoadedEventEnd = navTiming.domContentLoadedEventEnd || 0;
        const domContentLoadedEventStart = navTiming.domContentLoadedEventStart || 0;
        const domInteractive = navTiming.domInteractive || 0;

        // 有効な値が存在する場合のみパフォーマンスを記録
        if (loadEventEnd > 0 && fetchStart > 0 && loadEventEnd > fetchStart) {
          const loadTime = loadEventEnd - fetchStart;

          // 各メトリクスを安全に計算
          const domContentLoadedTime =
            domContentLoadedEventEnd > 0 &&
            domContentLoadedEventStart > 0 &&
            domContentLoadedEventEnd > domContentLoadedEventStart
              ? domContentLoadedEventEnd - domContentLoadedEventStart
              : undefined;

          const domInteractiveTime =
            domInteractive > 0 && fetchStart > 0 && domInteractive > fetchStart
              ? domInteractive - fetchStart
              : undefined;

          logger.performance('page_load', loadTime, {
            pageName,
            pathname: location.pathname,
            ...(domContentLoadedTime !== undefined && { domContentLoaded: domContentLoadedTime }),
            ...(domInteractiveTime !== undefined && { domInteractive: domInteractiveTime })
          });
        }
      }
    }
  }, [location, user, store]);
};

export default usePageTracking;
